/**
 * Fund variables editor — replaces the Excel sheet.
 * Table-based UI for editing fund configuration.
 *
 * Total Exposure is now auto-calculated: commitment / (participant_pct / 100).
 * Target IRR and Avg Bank Rate have been removed.
 * Area, Fund Strategy, and Product Type are dropdown-driven from lookup tables.
 */
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../Auth/AuthContext'
import { api } from '../../lib/api'

const LEVERAGE_FIELDS = new Set([
  'participant_pct', 'eqt_perf_loan_pct',
  'seb_loan_pct', 'eqt_perf_rate', 'seb_base_rate',
])

const INT_FIELDS = new Set([
  'payout_years', 'holding_period', 'first_investment_year',
])

const NUM_FIELDS = new Set([
  'total_commitment', 'leverage_ratio', 'participant_pct', 'eqt_perf_loan_pct',
  'seb_loan_pct', 'eqt_perf_rate', 'seb_base_rate', 'target_fund_moic',
  'roth_pct', 'traditional_ira_pct', 'taxable_pct', 'co_invest_amount', 'payout_years',
  'holding_period', 'first_investment_year',
])

const DROPDOWN_FIELDS = new Set([
  'fund_area_id', 'fund_strategy_id', 'product_type_id',
])

const COLUMNS = [
  { key: 'fund_name', label: 'Fund', editable: false },
  { key: 'fund_area_id', label: 'Area', type: 'dropdown' },
  { key: 'fund_strategy_id', label: 'Strategy', type: 'dropdown' },
  { key: 'product_type_id', label: 'Product Type', type: 'dropdown' },
  { key: 'investor_name', label: 'Vehicle', editable: false },
  { key: 'total_commitment', label: 'Commitment', type: 'currency' },
  { key: 'is_leveraged', label: 'Leveraged', type: 'bool' },
  { key: 'total_exposure', label: 'Total Exposure', type: 'currency', editable: false, calculated: true },
  { key: 'participant_pct', label: 'Participant %', type: 'pct' },
  { key: 'eqt_perf_loan_pct', label: 'EQT Perf %', type: 'pct' },
  { key: 'seb_loan_pct', label: 'SEB %', type: 'pct' },
  { key: 'eqt_perf_rate', label: 'EQT Rate', type: 'pct' },
  { key: 'seb_base_rate', label: 'SEB Rate', type: 'pct' },
  { key: 'target_fund_moic', label: 'Target MOIC', type: 'num' },
  { key: 'holding_period', label: 'Hold Period', type: 'int' },
  { key: 'first_investment_year', label: '1st Invest Yr', type: 'int' },
  { key: 'payout_years', label: 'Payout Yrs', type: 'int' },
  { key: 'roth_pct', label: 'Roth %', type: 'pct' },
  { key: 'traditional_ira_pct', label: 'Trad IRA %', type: 'pct' },
  { key: 'taxable_pct', label: 'Taxable %', type: 'pct' },
]

/** Calculate total exposure from commitment and participant %. */
function calcTotalExposure(commitment, participantPct, isLeveraged) {
  if (!isLeveraged) return commitment
  const pct = participantPct > 0 ? participantPct : 15
  return commitment / (pct / 100)
}

export default function FundVariables() {
  const { currentOrg } = useAuth()
  const [variables, setVariables] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [editValue, setEditValue] = useState('')

  // Lookup data for dropdowns
  const [areas, setAreas] = useState([])
  const [strategies, setStrategies] = useState([])
  const [productTypes, setProductTypes] = useState([])

  const orgId = currentOrg?.id

  const loadVariables = useCallback(async () => {
    if (!orgId) return
    try {
      const data = await api.get(`/funds/${orgId}/variables`)
      setVariables(data)
    } catch (e) {
      console.error('Failed to load fund variables:', e)
    } finally {
      setLoading(false)
    }
  }, [orgId])

  const loadLookups = useCallback(async () => {
    if (!orgId) return
    try {
      const [areasData, strategiesData, ptData] = await Promise.all([
        api.get(`/lookups/${orgId}/areas`),
        api.get(`/lookups/${orgId}/strategies`),
        api.get(`/lookups/${orgId}/product-types`),
      ])
      setAreas(areasData)
      setStrategies(strategiesData)
      setProductTypes(ptData)
    } catch (e) {
      console.error('Failed to load lookups:', e)
    }
  }, [orgId])

  useEffect(() => { loadVariables(); loadLookups() }, [loadVariables, loadLookups])

  async function saveField(fiId, field, value) {
    setEditing(null)
    try {
      await api.put(`/funds/${orgId}/variables/${fiId}`, { [field]: value })
      loadVariables()
    } catch (e) {
      console.error('Failed to save:', e)
    }
  }

  function commitEdit(fiId, field) {
    let val
    if (INT_FIELDS.has(field)) {
      val = parseInt(editValue, 10)
    } else if (NUM_FIELDS.has(field)) {
      val = parseFloat(editValue)
    } else {
      val = editValue
    }
    if (NUM_FIELDS.has(field) && isNaN(val)) {
      setEditing(null)
      return
    }
    saveField(fiId, field, val)
  }

  function startEdit(fiId, field, currentValue) {
    setEditing({ fiId, field })
    setEditValue(currentValue ?? '')
  }

  function handleKeyDown(e, fiId, field) {
    if (e.key === 'Enter') commitEdit(fiId, field)
    if (e.key === 'Escape') setEditing(null)
  }

  /** Get dropdown options for a field, possibly filtered by the row's current area. */
  function getDropdownOptions(col, v) {
    if (col.key === 'fund_area_id') return areas
    if (col.key === 'fund_strategy_id') {
      // Filter strategies by the fund's currently selected area
      const areaId = v.fund_area_id
      return areaId ? strategies.filter(s => s.fund_area_id === areaId) : strategies
    }
    if (col.key === 'product_type_id') return productTypes
    return []
  }

  function getDropdownDisplayValue(col, v) {
    if (col.key === 'fund_area_id') return v.fund_areas?.name || '-'
    if (col.key === 'fund_strategy_id') return v.fund_strategies?.name || '-'
    if (col.key === 'product_type_id') return v.product_types?.name || '-'
    return '-'
  }

  function formatValue(col, value) {
    if (col.type === 'bool') return value ? 'Yes' : 'No'
    if (col.type === 'currency') return value != null ? Number(value).toLocaleString() : '-'
    if (col.type === 'pct') return value != null ? `${value}%` : '-'
    if (col.type === 'num') return value != null ? `${value}x` : '-'
    if (col.type === 'int') return value != null ? `${value}` : '-'
    return value ?? '-'
  }

  if (loading) {
    return <div className="p-6 text-ink-muted">Loading fund variables...</div>
  }

  return (
    <div className="py-2 sm:py-4">
      <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 sm:mb-6">Fund Variables</h2>
      <p className="text-sm text-ink-muted mb-4 sm:mb-6">
        Click any cell to edit. Press Enter to save, Escape to cancel. Total Exposure is auto-calculated from Commitment and Participant %.
      </p>

      <div className="scroll-x" style={{ WebkitOverflowScrolling: 'touch' }}>
        <table className="w-full min-w-[800px] text-xs">
          <thead>
            <tr className="border-b border-edge">
              {COLUMNS.map(col => (
                <th key={col.key} className="text-left py-2.5 sm:py-3 px-2 text-ink-muted font-medium whitespace-nowrap">
                  {col.label}
                  {col.calculated && <span className="text-ink-subtle text-[10px] ml-0.5">(calc)</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {variables.map(v => {
              const fi = v.fund_investments
              const fiId = fi.id
              const isLeveraged = v.is_leveraged
              const commitment = Number(fi.total_commitment) || 0
              const participantPct = isLeveraged ? (Number(v.participant_pct) || 15) : 100
              const totalExposure = calcTotalExposure(commitment, participantPct, isLeveraged)

              return (
                <tr key={v.id} className="border-b border-edge-subtle hover:bg-surface-tertiary/50">
                  {COLUMNS.map(col => {
                    let value
                    if (col.key === 'fund_name') value = fi.funds.fund_name
                    else if (col.key === 'investor_name') value = fi.investors.investor_name
                    else if (col.key === 'total_commitment') value = fi.total_commitment
                    else if (col.key === 'total_exposure') value = totalExposure
                    else value = v[col.key]

                    const isLeverageField = LEVERAGE_FIELDS.has(col.key)
                    const isEditing = editing?.fiId === fiId && editing?.field === col.key
                    const isDropdown = col.type === 'dropdown'
                    const isCalculated = col.calculated
                    const isReadonly = col.editable === false || isCalculated

                    // Non-leveraged: leverage-specific fields are not editable
                    if (isLeverageField && !isLeveraged) {
                      let display = '—'
                      if (col.key === 'participant_pct') display = '100%'
                      return (
                        <td key={col.key} className="py-2 px-2 text-ink-subtle whitespace-nowrap">
                          {display}
                        </td>
                      )
                    }

                    // Calculated total_exposure for non-leveraged — just show commitment
                    if (col.key === 'total_exposure' && !isLeveraged) {
                      return (
                        <td key={col.key} className="py-2 px-2 text-ink-subtle whitespace-nowrap">
                          {Number(commitment).toLocaleString()}
                        </td>
                      )
                    }

                    // Dropdown fields (Area, Strategy, Product Type)
                    if (isDropdown) {
                      const options = getDropdownOptions(col, v)
                      const displayVal = getDropdownDisplayValue(col, v)

                      return (
                        <td key={col.key} className="py-2 px-2 whitespace-nowrap">
                          {isEditing ? (
                            <select
                              value={editValue || ''}
                              onChange={(e) => {
                                const newVal = e.target.value || null
                                saveField(fiId, col.key, newVal)
                                // If area changed, clear strategy
                                if (col.key === 'fund_area_id' && v.fund_strategy_id) {
                                  saveField(fiId, 'fund_strategy_id', null)
                                }
                              }}
                              className="bg-surface-tertiary text-ink px-1 py-0.5 rounded text-xs"
                              autoFocus
                              onBlur={() => setEditing(null)}
                            >
                              <option value="">—</option>
                              {options.map(opt => (
                                <option key={opt.id} value={opt.id}>{opt.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span
                              className="text-ink-muted cursor-pointer hover:text-ink"
                              onClick={() => startEdit(fiId, col.key, value)}
                            >
                              {displayVal}
                            </span>
                          )}
                        </td>
                      )
                    }

                    const editable = !isReadonly

                    return (
                      <td
                        key={col.key}
                        className={`py-2 px-2 whitespace-nowrap ${editable ? 'cursor-pointer hover:bg-surface-secondary/50' : ''} ${isCalculated ? 'text-ink-subtle italic' : ''}`}
                        onClick={() => editable && startEdit(fiId, col.key, value)}
                      >
                        {isEditing ? (
                          col.type === 'bool' ? (
                            <select
                              value={editValue ? 'true' : 'false'}
                              onChange={(e) => saveField(fiId, col.key, e.target.value === 'true')}
                              className="bg-surface-tertiary text-ink px-1 py-0.5 rounded text-xs"
                              autoFocus
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, fiId, col.key)}
                              onBlur={() => commitEdit(fiId, col.key)}
                              className="bg-surface-tertiary text-ink px-1 py-0.5 rounded text-xs w-full"
                              autoFocus
                            />
                          )
                        ) : (
                          <span className={`${col.key === 'fund_name' ? 'text-ink font-medium' : isCalculated ? '' : 'text-ink-muted'}`}>
                            {formatValue(col, value)}
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
